/**
 * StudyPlan (Sprint 26) — page "Kế hoạch học tập", thay mục nav "Môn học".
 *
 * Hub chọn 1 trong các ngành user đang/đã theo (`useMyMajors`) — ngành
 * "enrolled" luôn xếp trước (ưu tiên hiển thị ngành hiện tại), rồi hiện
 * khung chương trình theo kỳ (`MajorTermsCard`, dùng chung với
 * `MajorPublicDetail`) + gợi ý đăng ký môn học kỳ hiện tại
 * (`SuggestedCourses`, chỉ hiện khi ngành đang chọn còn "enrolled" — ngành
 * đã tốt nghiệp/rời không cho đăng ký thêm).
 */

import { useMemo, useState } from "react";
import { Breadcrumb, Empty, Result, Segmented, Space, Spin, Tag, Typography } from "antd";
import { HomeOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { useMajorDetail } from "@/api/catalog";
import {
  useMajorCourseEligibility,
  useMajorCredits,
  useMyMajors,
  useSuggestedCourses,
  type MajorEnrollmentStatus,
} from "@/api/enrollment";
import { SuggestedCourses, type CatalogEntry } from "@/components/SuggestedCourses";
import { MajorTermsCard, type CourseEligibilityInfo } from "@/components/MajorTermsCard";
import { MajorCreditsSummary } from "@/components/MajorCreditsSummary";
import { useCourseCart } from "@/hooks/useCourseCart";

const { Title, Text, Paragraph } = Typography;

const STATUS_LABEL: Record<MajorEnrollmentStatus, string> = {
  enrolled: "Đang theo học",
  completed: "Đã tốt nghiệp",
  dropped: "Đã rời ngành",
};

const STATUS_COLOR: Record<MajorEnrollmentStatus, string> = {
  enrolled: "processing",
  completed: "success",
  dropped: "default",
};

export function StudyPlan() {
  const { data: majors, isLoading, error } = useMyMajors();
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);

  // Ngành "enrolled" lên đầu (ưu tiên hiển thị ngành hiện tại), trong từng
  // nhóm trạng thái sort theo `enrolled_at` gần nhất trước.
  const sortedMajors = useMemo(() => {
    if (!majors) return [];
    return [...majors].sort((a, b) => {
      if (a.status !== b.status) {
        if (a.status === "enrolled") return -1;
        if (b.status === "enrolled") return 1;
      }
      return new Date(b.enrolled_at).getTime() - new Date(a.enrolled_at).getTime();
    });
  }, [majors]);

  const activeSlug = selectedSlug ?? sortedMajors[0]?.major.slug;
  const activeMyMajor = sortedMajors.find((m) => m.major.slug === activeSlug);

  const { data: majorDetail, isLoading: isMajorLoading } = useMajorDetail(activeSlug);
  // Ngành đã tốt nghiệp/rời (`completed`/`dropped`) không cho đăng ký thêm —
  // undefined tắt cả 3 hook dưới (gợi ý/eligibility/giỏ hàng), khớp gate cũ
  // đang áp cho `SuggestedCourses`.
  const enrolledMajorId = activeMyMajor?.status === "enrolled" ? majorDetail?.id : undefined;
  const { data: suggestions } = useSuggestedCourses(enrolledMajorId);
  // Sprint 35 — "học vượt": trạng thái đăng ký (blocked/giá) của TOÀN BỘ môn
  // mọi kỳ + giỏ hàng dùng CHUNG giữa `SuggestedCourses` (kỳ hiện tại) và
  // `MajorTermsCard` (mọi kỳ) — 1 giỏ, 1 lần checkout cho cả 2 nguồn chọn môn.
  const { data: eligibility } = useMajorCourseEligibility(enrolledMajorId);
  // Sếp yêu cầu 2026-07-13: tổng tín chỉ đã tích lũy + còn thiếu (bắt buộc/tự
  // chọn), đối chiếu mục tiêu ngành.
  const { data: majorCredits } = useMajorCredits(enrolledMajorId);
  const sharedCart = useCourseCart(enrolledMajorId);

  // Map slug → tên/giá/loại (từ `majorDetail.terms`, đã có mọi kỳ) +
  // `effective_price_cents` (từ `eligibility`, chỉ endpoint mới trả) — cho
  // `SuggestedCourses` tra cứu hiện đúng thông tin môn "học vượt" trong Modal
  // giỏ hàng dù môn đó không thuộc kỳ hiện tại.
  const catalogLookup = useMemo(() => {
    const map = new Map<string, CatalogEntry>();
    for (const term of majorDetail?.terms ?? []) {
      for (const course of term.courses) {
        map.set(course.slug, {
          code: course.code,
          name: course.name,
          slug: course.slug,
          is_required: course.is_required,
          effective_price_cents: null,
        });
      }
    }
    for (const c of eligibility?.courses ?? []) {
      const existing = map.get(c.slug);
      if (existing) map.set(c.slug, { ...existing, effective_price_cents: c.effective_price_cents });
    }
    return map;
  }, [majorDetail, eligibility]);

  const eligibilityBySlug = useMemo(() => {
    const map = new Map<string, CourseEligibilityInfo>();
    for (const c of eligibility?.courses ?? []) {
      map.set(c.slug, {
        blocked: c.blocked,
        missingPrerequisites: c.missing_prerequisites,
        effectivePriceCents: c.effective_price_cents,
      });
    }
    return map;
  }, [eligibility]);

  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }}>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          {
            title: (
              <Link to="/">
                <HomeOutlined />
              </Link>
            ),
          },
          { title: "Kế hoạch học tập" },
        ]}
      />
      <Title level={2}>Kế hoạch học tập</Title>

      {isLoading && (
        <div style={{ textAlign: "center", padding: 64 }}>
          <Spin size="large" />
        </div>
      )}

      {error && <Result status="error" title="Không tải được danh sách ngành học" />}

      {!isLoading && !error && sortedMajors.length === 0 && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Bạn chưa được gán vào ngành học nào. Liên hệ quản trị viên để được xếp ngành."
        />
      )}

      {!isLoading && sortedMajors.length > 0 && (
        <>
          <Segmented
            style={{ marginBottom: 16 }}
            value={activeSlug}
            onChange={(value) => setSelectedSlug(value as string)}
            options={sortedMajors.map((m) => ({
              label: (
                <Space size={4}>
                  {m.major.name}
                  <Tag color={STATUS_COLOR[m.status]} style={{ marginRight: 0 }}>
                    {STATUS_LABEL[m.status]}
                  </Tag>
                </Space>
              ),
              value: m.major.slug,
            }))}
          />

          {isMajorLoading && (
            <div style={{ textAlign: "center", padding: 32 }}>
              <Spin />
            </div>
          )}

          {!isMajorLoading && majorDetail && (
            <>
              <Space size="small" wrap style={{ marginBottom: 4 }}>
                <Tag color="blue">{majorDetail.code}</Tag>
                <Text type="secondary">{majorDetail.program_name}</Text>
              </Space>
              {majorDetail.description && (
                <Paragraph type="secondary">{majorDetail.description}</Paragraph>
              )}

              {activeMyMajor?.status === "enrolled" && majorCredits?.credits && (
                <MajorCreditsSummary credits={majorCredits.credits} />
              )}

              {activeMyMajor?.status === "enrolled" && (
                <SuggestedCourses
                  majorId={majorDetail.id}
                  variant="full"
                  cart={sharedCart}
                  catalogLookup={catalogLookup}
                />
              )}

              <MajorTermsCard
                terms={majorDetail.terms}
                currentTermId={suggestions?.current_term?.id ?? null}
                cart={
                  activeMyMajor?.status === "enrolled"
                    ? { selected: sharedCart.selected, toggle: sharedCart.toggle }
                    : undefined
                }
                eligibilityBySlug={
                  activeMyMajor?.status === "enrolled" ? eligibilityBySlug : undefined
                }
              />
            </>
          )}
        </>
      )}
    </div>
  );
}

export default StudyPlan;
